<script setup lang="ts">
import { useI18n } from 'vue-i18n'

/** Questions a first-time visitor actually asks before trusting a tool like this. */
const QUESTIONS = ['cost', 'data', 'install', 'accounts'] as const

const { t } = useI18n()
</script>

<template>
  <section class="faq" aria-labelledby="faq-heading">
    <h2 id="faq-heading">{{ t('home.faq.heading') }}</h2>
    <div class="faq__list">
      <!-- <details> keeps every answer in the HTML for crawlers, and needs no script. -->
      <details v-for="key in QUESTIONS" :key="key" class="faq__item">
        <summary>{{ t(`home.faq.${key}.question`) }}</summary>
        <p>{{ t(`home.faq.${key}.answer`) }}</p>
      </details>
    </div>
  </section>
</template>

<style scoped>
h2 {
  margin: 0 0 var(--space-4);
  font-size: 1.35rem;
}

.faq__list {
  max-width: 54rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
}

.faq__item + .faq__item {
  border-top: 1px solid var(--color-border);
}

.faq__item summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-6);
  font-weight: 600;
  cursor: pointer;
  list-style: none;
}

.faq__item summary::-webkit-details-marker {
  display: none;
}

/* A chevron that turns when open. */
.faq__item summary::after {
  content: '';
  flex: none;
  width: 0.5rem;
  height: 0.5rem;
  border: solid var(--color-text-muted);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) translate(-2px, -2px);
  transition: transform var(--transition-fast);
}

.faq__item[open] summary::after {
  transform: rotate(-135deg) translate(-2px, -2px);
}

.faq__item summary:hover {
  color: var(--color-accent);
}

.faq__item p {
  margin: 0;
  padding: 0 var(--space-6) var(--space-4);
  color: var(--color-text-muted);
}
</style>
